/* eslint-disable no-console */
import _ from 'lodash';
import ResourceTranslation from './ResourceTranslation.js';
import DisplayNameResolver from './DisplayNameResolver.js';

export default class Language {
  static _current = 'fr';

  static _resourceDisplayNameResolver = new DisplayNameResolver();

  static _resourceTranslator = new ResourceTranslation(this._resourceDisplayNameResolver);

  static _translations = new Map();

  static init(translationDefinitions) {
    translationDefinitions.forEach(definition => {
      this._translations.set(
        definition.feature.id,
        new Translation(definition, createDisplayNameResolver(definition), this._resourceTranslator)
      );
    });
  }

  static setResources(resources) {
    this._resourceTranslator._resources = resources;
    this._resourceDisplayNameResolver.addAcronyms(resources.acronyms || []);
    this._resourceDisplayNameResolver.addExplicits(resources.techFr || {});
    this._resourceDisplayNameResolver.addExplicits(resources.techEn || {});
  }

  static get current() {
    return this._current;
  }

  static set current(value) {
    this._current = value;
  }

  static reset() {
    this._current = 'fr';
    this._resourceDisplayNameResolver = new DisplayNameResolver();
    this._resourceTranslator = new ResourceTranslation(this._resourceDisplayNameResolver);
    this._translations = new Map();
  }

  static get(text, options = {}) {
    if (!options.featureId) {
      console.warn('[i18n] No feature specified. Fallback to JSON resources.');
    }

    options = _.defaults(options, { featureId: 'resources', lang: this._current });

    return this.getTranslation(options.featureId).get(text, options.lang);
  }

  static getTranslation(featureId) {
    if (!this._translations.has(featureId)) {
      this._translations.set(featureId, this._resourceTranslator);
    }

    return this._translations.get(featureId);
  }
}

export class Translation {
  constructor(translation, displayNameResolver, parent) {
    this._translation = translation;
    this._displayNameResolver = displayNameResolver;
    this._parent = parent;
  }

  get(text, languageCode = Language.current, options = {}) {
    options = _.defaults(options, { diag: { featureId: this._translation.feature.id } });

    const displayName = this._resolve(text, options);

    if (!this._translation.supportedLanguages.has(e => e.language === languageCode)) {
      return displayName;
    }

    return (
      this._translate(displayName, languageCode) ||
      (this._parent ? this._parent.get(displayName, languageCode, options) : displayName)
    );
  }

  _resolve(text, options) {
    const {
      diag: { featureId }
    } = _.defaults(options, { diag: {} });

    let [displayName, resolved] = this._displayNameResolver.resolve(text);

    if (resolved) {
      return displayName;
    }

    [displayName, resolved] = Language._resourceDisplayNameResolver.resolve(text);

    if (resolved) {
      console.warn(
        `[i18n] [${featureId}] resolved technical with resources "${text}": "${displayName}"`
      );
    }

    return displayName;
  }

  _translate(text, languageCode) {
    const resource = this._translation.entries.get(e => e.key === text);

    if (resource) {
      return _.get(
        resource.entries.get(e => e.languageCode === languageCode),
        ['value']
      );
    }
  }
}

function createDisplayNameResolver(translation) {
  const { acronyms, entries, defaultLanguage } = translation;
  const explicits = entries
    .filter(entry => entry.entries.has(resource => resource.languageCode === defaultLanguage))
    .reduce((accumulator, entry) => {
      const value = entry.entries.get(
        languageEntry => languageEntry.languageCode === defaultLanguage
      ).value;

      accumulator[entry.key] = value;
      return accumulator;
    }, {});

  return new DisplayNameResolver(explicits, acronyms.map('acronym'));
}
